import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import ScoreIndicator, { getHealthColor } from './ScoreIndicator';
import { CHART_COLORS } from '../shared';
import './CategoryCard.css';

interface QuestionData {
  questionId: string;
  text: string;
  totalResponses: number;
  averageScore: number;
  percentPositive: number;
  distribution: Record<string, number>;
}

interface CategoryCardProps {
  categoryId: string;
  categoryName: string;
  questionCount: number;
  totalResponses: number;
  averageScore: number;
  percentPositive: number;
  healthIndicator: 'green' | 'yellow' | 'red';
  questions: QuestionData[];
  defaultExpanded?: boolean;
}

const formatAnswer = (value: string): string => {
  return value
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const CategoryCard: React.FC<CategoryCardProps> = ({
  categoryName,
  questionCount,
  totalResponses,
  averageScore,
  percentPositive,
  questions,
  defaultExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const prepareChartData = (distribution: Record<string, number>) => {
    const order = ['always', 'most-of-the-time', 'sometimes', 'never'];
    return order
      .filter((key) => distribution[key] !== undefined)
      .map((key) => ({
        name: formatAnswer(key),
        value: distribution[key],
      }));
  };

  return (
    <div className="category-card">
      <button
        className="category-card__header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className="category-card__title-section">
          <h3 className="category-card__title">{categoryName}</h3>
          <span className="category-card__meta">
            {questionCount} questions | ~{Math.round(totalResponses)} responses
          </span>
        </div>
        <div className="category-card__scores">
          <ScoreIndicator
            percentPositive={percentPositive}
            averageScore={averageScore}
            size="medium"
          />
        </div>
        <div className={`category-card__expand-icon ${isExpanded ? 'expanded' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M5 8L10 13L15 8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="category-card__content">
          {questions.map((question) => (
            <div key={question.questionId} className="category-card__question">
              <div className="category-card__question-header">
                <p className="category-card__question-text">{question.text}</p>
                <div className="category-card__question-scores">
                  <span
                    className="category-card__question-percent"
                    style={{ color: getHealthColor(question.percentPositive) }}
                  >
                    {Math.round(question.percentPositive)}%
                  </span>
                  <span className="category-card__question-avg">
                    ({question.averageScore.toFixed(1)})
                  </span>
                </div>
              </div>
              <div className="category-card__question-chart">
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart
                    data={prepareChartData(question.distribution)}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis
                      type="number"
                      domain={[0, 100]}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 11, fill: '#6d6d70' }}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fontSize: 11, fill: '#1d1d1f' }}
                      width={95}
                    />
                    <Tooltip
                      formatter={(value) => [`${value}%`, 'Responses']}
                      contentStyle={{
                        background: '#fff',
                        border: '1px solid #e5e5ea',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {prepareChartData(question.distribution).map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHART_COLORS.series[index % CHART_COLORS.series.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoryCard;
